import { apiRequestResponseMsg, defaultSsrSettings } from '@va/constants';
import { createSsrSettings } from '@va/dashboard/actions/api';
import { isTrackingCodeInstalled as isTrackingCodeInstalledSelector } from '@va/dashboard/selectors/app';
import { getSsrSettingsSelector, isPremium } from '@va/dashboard/selectors/core';
import { getSupportCenterDomain } from '@va/dashboard/selectors/customization';
import { isMobileDevice } from '@va/dashboard/selectors/ui';
import { SettingsHeading } from '@va/dashboard/ui/pages';
import { useTrackingCodeVerifier } from '@va/dashboard/util-hooks';
import { LineGraphIcon, SearchIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { Button, Paragraph, fontWeights, paragraphSizes } from '@va/ui/design-system';
import classNames from 'classnames';
import { isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

export const TrackingCodeCardWix = () => {
  const isPremiumWebsite = useSelector(isPremium);
  const ssrSettings = useSelector(getSsrSettingsSelector);
  const dispatch = useDispatch();

  const onTrackingCodeSuccess = useCallback(() => {
    if (!isPremiumWebsite) return;
    if (ssrSettings?.error?.message === apiRequestResponseMsg.NOT_FOUND_SSR_SETTINGS) {
      dispatch(createSsrSettings(defaultSsrSettings));
    }
  }, [dispatch, isPremiumWebsite, ssrSettings?.error?.message]);

  const { verify } = useTrackingCodeVerifier(onTrackingCodeSuccess);
  const translate = useTranslate();
  const isMobile = useSelector(isMobileDevice);
  const isTrackingCodeInstalled = useSelector(isTrackingCodeInstalledSelector);
  const history = useHistory();
  const supportCenterDomain = useSelector(getSupportCenterDomain);

  const trackingCodeStatusText = useMemo(() => {
    // A different message is required for each of those conditions
    if (isNil(isTrackingCodeInstalled)) return translate('modal.addWixSsrCode.installError');
    if (isTrackingCodeInstalled === false) return translate('modal.addWixSsrCode.unInstalled');
    return translate('modal.addWixSsrCode.successInstalled');
  }, [isTrackingCodeInstalled, translate]);

  const embedWixSsrCode = () => {
    history.push('/settings/tracking-code');
  };

  return (
    <div className='flex flex-col gap-18px items-start'>
      <SettingsHeading
        title={translate('card.ssrStatus.title')}
        subText={translate('card.trackingCode.info')}
        tooltip={translate('card.installSsr.tooltip', { supportCenterDomain })}
      />
      <div className='flex flex-col gap-4 w-full'>
        <div className='rounded-18 p-12px bg-white-snow flex items-center gap-3'>
          <div className='rounded-full w-12 h-12 bg-white flex items-center justify-center'>
            <LineGraphIcon />
          </div>
          <div>
            <Paragraph colorClassName='text-gray-devil' size={paragraphSizes.tiny} weight={fontWeights.medium}>
              {translate('card.ssrStatus.trackingCodeStatus')}
            </Paragraph>
            <Paragraph
              className={classNames({
                'text-gray-charcoal': !isTrackingCodeInstalled,
                'text-green-pure': isTrackingCodeInstalled,
              })}
              weight={fontWeights.medium}
            >
              {trackingCodeStatusText}
            </Paragraph>
          </div>
          <Button
            onClick={verify}
            className='ml-auto bg-white! text-gray-charcoal! w-12 h-12 sm-initial:w-62.25 justify-center'
            icon={() => <SearchIcon />}
            text={isMobile ? undefined : translate('modal.addWixSsrCode.verifyInstallation')}
          />
        </div>
        <Button
          disabled={isTrackingCodeInstalled}
          onClick={embedWixSsrCode}
          color='tertiary'
          text={translate('card.trackingCode.embedCode')}
          tooltip={isTrackingCodeInstalled ? translate('modal.addWixSsrCode.successInstalled') : undefined}
        />
      </div>
    </div>
  );
};
